import {
    AppBar,
    Box,
    IconButton,
    Stack,
    Tab,
    Tabs,
    Toolbar,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";

import {
    usePopupState,
    bindTrigger,
    bindMenu,
} from "material-ui-popup-state/hooks";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { coreActions } from "./store/slice";
import {
    selectHideNavigationalElements,
    selectShowSidebar,
} from "./store/selectors";

import ICoppLogo from "./ICoppLogo";
import BasicFitLogo from "./BasicFitLogo";
import { MuiTabNavLink } from "./MuiTabNavLink";

import { SiteSearch } from "features/sites/SiteSearch";
import { appFeaturesSelectors } from "features/appFeatures/store/selectors";
import { AppFeature } from "features/appFeatures/types/AppFeature";
import UserMenu from "../user/UserMenu";
import LoggedInUserButton from "./LoggedInUserButton";

export interface HeaderBarProps {
    drawerWidth: number;
}

export default function HeaderBar(props: HeaderBarProps) {
    const dispatch = useAppDispatch();

    const showSidebar = useAppSelector(selectShowSidebar);

    const userHasLiveStreamFeature = useAppSelector((state) =>
        appFeaturesSelectors.selectUserHasFeatures(state, [
            AppFeature.LiveStreaming,
        ]),
    );
    const userHasPlaybackFeature = useAppSelector((state) =>
        appFeaturesSelectors.selectUserHasFeatures(state, [
            AppFeature.Playback,
        ]),
    );
    const userHasCaseManagementFeature = useAppSelector((state) =>
        appFeaturesSelectors.selectUserHasFeatures(state, [
            AppFeature.CaseManagement,
        ]),
    );
    const userHasDiagnosticsFeature = useAppSelector((state) =>
        appFeaturesSelectors.selectUserHasFeatures(state, [
            AppFeature.Diagnostics,
        ]),
    );

    const hideNavigationalElements = useAppSelector(
        selectHideNavigationalElements,
    );

    const popupState = usePopupState({
        variant: "popover",
        popupId: "userMenu",
    });

    return (
        <AppBar
            position="fixed"
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        >
            <Toolbar
                sx={{
                    paddingLeft: "0 !important",
                }}
            >
                <Stack
                    direction={"row"}
                    sx={{
                        flexShrink: 0,
                        flexGrow: 0,
                        width: props.drawerWidth,
                        alignItems: "center",
                    }}
                >
                    <IconButton
                        size="large"
                        edge="start"
                        color="inherit"
                        sx={{
                            marginLeft: 1,
                            marginRight: 1,
                        }}
                        onClick={() => {
                            dispatch(
                                coreActions.setShowSidebar({
                                    show: !showSidebar,
                                }),
                            );
                        }}
                    >
                        {!showSidebar && <MenuIcon />}
                        {showSidebar && <KeyboardDoubleArrowLeftIcon />}
                    </IconButton>
                    <ICoppLogo />
                </Stack>
                {!hideNavigationalElements && (
                    <SiteSearch
                        sx={{
                            minWidth: 250,
                            maxWidth: 600,
                            flexShrink: 1,
                            flexGrow: 1,
                        }}
                        minSearchQueryCharacterLength={2}
                    />
                )}
                {!hideNavigationalElements && (
                    <Tabs
                        sx={{ marginLeft: 2 }}
                        value={false}
                        textColor="inherit"
                        indicatorColor="secondary"
                    >
                        <Tab
                            label="Live streaming"
                            to="/livestreaming"
                            component={MuiTabNavLink}
                            disabled={!userHasLiveStreamFeature}
                        />
                        <Tab
                            label="Playback"
                            to="/playback"
                            component={MuiTabNavLink}
                            disabled={!userHasPlaybackFeature}
                        />
                        <Tab
                            label="Cases"
                            to="/cases"
                            component={MuiTabNavLink}
                            disabled={!userHasCaseManagementFeature}
                        />
                        <Tab
                            label="Diagnostics"
                            to="/diagnostics"
                            component={MuiTabNavLink}
                            disabled={!userHasDiagnosticsFeature}
                        />
                    </Tabs>
                )}

                <Box sx={{ flexGrow: 1, flexShrink: 1 }}></Box>
                <BasicFitLogo sx={{ marginRight: 2 }} />
                <LoggedInUserButton {...bindTrigger(popupState)} />
                <UserMenu {...bindMenu(popupState)} />
            </Toolbar>
        </AppBar>
    );
}
